/**
 * A text input.
 *
 * Errors are wired through `aria-invalid` and `aria-describedby` rather than
 * shown only in red text: PRD §9.7 forbids conveying state by colour alone,
 * and a field whose error a screen reader never announces is a field that
 * cannot be corrected.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.6, PRD §9.7
 */
import { cn } from '../../lib/cn';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Id of the element describing this field's error, when there is one. */
  errorId?: string;
  invalid?: boolean;
}

export function Input({
  className,
  errorId,
  invalid = false,
  ...props
}: InputProps): React.JSX.Element {
  return (
    <input
      className={cn(
        'font-body min-h-11 w-full rounded-md border px-3 text-sm',
        'placeholder:text-gray-400',
        invalid ? 'border-sem-bad' : 'border-gray-200',
        'disabled:bg-gray-100 disabled:opacity-60',
        className,
      )}
      aria-invalid={invalid || undefined}
      aria-describedby={invalid ? errorId : undefined}
      {...props}
    />
  );
}
